// ============================================================================
// Adam Platform — Staged Upgrade Progress (BUCKET I.8)
// ============================================================================
// Renders the multi-stage canary progress for the active upgrade. Falls back
// to the legacy "batch X/Y" copy when the CP response doesn't include a
// stages[] array (e.g. single-stage upgrade, or ambiguous segmentation).
//
// P4 compliance: if stages is empty we DON'T fabricate a stage array —
// we show the simpler batch copy so the UI never lies about having multi-
// stage visibility.
// ============================================================================
import { Card, SectionHeader, Badge } from "./index";

export interface UpgradeStage {
  pct: number;
  status: "pending" | "in_progress" | "healthy" | "failed";
  deploymentsUpgraded: number;
  deploymentsTotal: number;
  healthGateStatus?: "passing" | "failing";
}

export interface ActiveUpgradeView {
  id: string;
  fromVersion: string;
  toVersion: string;
  currentBatch: number;
  totalBatches: number;
  tenantsUpgraded: number;
  tenantsTotal: number;
  status: string;
  stages?: UpgradeStage[];
}

interface StagedUpgradeProgressProps {
  upgrade: ActiveUpgradeView;
}

const STAGE_BADGE_COLOR: Record<UpgradeStage["status"], "green" | "amber" | "gray" | "red"> = {
  healthy: "green",
  in_progress: "amber",
  pending: "gray",
  failed: "red",
};

function stageStatusLabel(s: UpgradeStage["status"]): string {
  switch (s) {
    case "healthy":
      return "healthy";
    case "in_progress":
      return "in progress";
    case "pending":
      return "pending";
    case "failed":
      return "failed";
  }
}

/**
 * Pick the "active" stage for highlight purposes. First in_progress wins;
 * else first pending; else none highlighted.
 */
function findActiveStageIndex(stages: UpgradeStage[]): number {
  const inProgress = stages.findIndex((s) => s.status === "in_progress");
  if (inProgress !== -1) return inProgress;
  const pending = stages.findIndex((s) => s.status === "pending");
  return pending;
}

export function StagedUpgradeProgress({ upgrade }: StagedUpgradeProgressProps) {
  const stages = upgrade.stages ?? [];
  const hasStages = stages.length > 0;

  return (
    <Card className="mb-6">
      <SectionHeader
        title="Upgrade progress"
        subtitle={`${upgrade.fromVersion} → ${upgrade.toVersion}`}
      />

      {hasStages ? <StagesList stages={stages} /> : <LegacyBatchProgress upgrade={upgrade} />}
    </Card>
  );
}

function StagesList({ stages }: { stages: UpgradeStage[] }) {
  const activeIdx = findActiveStageIndex(stages);

  return (
    <div className="space-y-2" data-testid="staged-progress-list">
      {stages.map((s, idx) => {
        const isActive = idx === activeIdx;
        return (
          <div
            key={idx}
            data-testid={`staged-progress-row-${idx}`}
            className="rounded-md px-3 py-2 flex items-center gap-4"
            style={{
              border: "1px solid var(--c-border)",
              backgroundColor: isActive
                ? "color-mix(in srgb, var(--c-primary) 6%, transparent)"
                : "transparent",
            }}
          >
            <div style={{ minWidth: 80 }}>
              <div className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                Stage {idx + 1}
              </div>
              <div className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                {s.pct}%
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                {s.deploymentsUpgraded} of {s.deploymentsTotal} upgraded
              </div>
              {s.healthGateStatus && (
                <div className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                  Health gate: {s.healthGateStatus}
                </div>
              )}
            </div>
            <Badge color={STAGE_BADGE_COLOR[s.status]}>{stageStatusLabel(s.status)}</Badge>
          </div>
        );
      })}
    </div>
  );
}

function LegacyBatchProgress({ upgrade }: { upgrade: ActiveUpgradeView }) {
  return (
    <div
      className="rounded-md px-3 py-2 text-xs"
      data-testid="legacy-batch-progress"
      style={{
        border: "1px solid var(--c-border)",
        color: "var(--c-text-secondary)",
      }}
    >
      Batch {upgrade.currentBatch} of {upgrade.totalBatches} · {upgrade.tenantsUpgraded} of{" "}
      {upgrade.tenantsTotal} deployments upgraded
      <div className="mt-1" style={{ color: "var(--c-text-faint)" }}>
        Multi-stage data not available for this upgrade.
      </div>
    </div>
  );
}

export default StagedUpgradeProgress;
